import { currency } from '@/states';
import { getExcursionSlug } from '@/utils/slug';
import { Card, CardBody, CardFooter, CardTitle, Image } from 'react-bootstrap';
import {
  FaArrowRight,
  FaBusAlt,
  FaShip,
  FaLeaf,
  FaTag,
} from 'react-icons/fa';
import { FaPersonHiking } from 'react-icons/fa6';
import { BsBuilding, BsHeadset, BsCompass } from 'react-icons/bs';
import { FiCalendar, FiClock, FiCoffee } from 'react-icons/fi';
import Link from 'next/link';
import { useSearchParams } from '@/compat/react-router-dom';
import { toImageSrc, toPathSegment } from '@/utils/imageUrl';

const TourCard = ({ tour, excursionBasePath = '/excursions', urgency = null }) => {
  const {
    daysOfWeekText,
    name,
    tagline,
    price,
    type,
    typeIcon,
    image,
    durationText,
    pricingUnit,
    pickupAvailable,
    lunchIncludedText,
    wineOrBeerIncludedText,
    tourGuideLanguages,
    licensedGuideNames
  } = tour;

  const [searchParams] = useSearchParams();
  const slug = tour.slug || getExcursionSlug(name);
  const safeSlug = toPathSegment(slug, '');
  const carriedParams = new URLSearchParams();
  ['date', 'dateFrom', 'dateTo', 'adults', 'children', 'infants', 'language', 'pickupArea'].forEach((key) => {
    const value = searchParams.get(key);
    if (value) carriedParams.set(key, value);
  });
  if (!carriedParams.get('date') && carriedParams.get('dateFrom')) {
    carriedParams.set('date', carriedParams.get('dateFrom'));
  }
  const carriedQuery = carriedParams.toString();
  const detailPath = safeSlug
    ? `${String(excursionBasePath || '/excursions').replace(/\/$/, '')}/${encodeURIComponent(safeSlug)}`
    : String(excursionBasePath || '/excursions').replace(/\/$/, '');
  const detailUrl = carriedQuery ? `${detailPath}?${carriedQuery}` : detailPath;
  const hasTourGuideIncluded = (tourGuideLanguages && tourGuideLanguages.length > 0) || (licensedGuideNames && licensedGuideNames.length > 0);
  const normalizedType = typeLabelForIcon(type || '');
  const TypeIcon = getExcursionTypeIconComponent(typeIcon, normalizedType);
  const typeColorKey = getTypeColorKey(normalizedType);
  const imageSrc = toImageSrc(image);

  const scheduleRowParts = [];
  const normalizedDaysOfWeekText = (() => {
    const raw = String(daysOfWeekText || '').trim();
    if (!raw) return '';
    if (/^every\s+day$/i.test(raw)) return 'Every day';
    return raw.replace(/^every\s+/i, '').trim();
  })();
  if (normalizedDaysOfWeekText) {
    scheduleRowParts.push(
      <span key="dow" className="info-item-text">
        <FiCalendar size={18} className="info-icon" aria-hidden />
        <span>{normalizedDaysOfWeekText}</span>
      </span>
    );
  }
  if (pickupAvailable) {
    scheduleRowParts.push(
      <span key="pickup" className="info-item-text">
        <BsBuilding size={18} className="info-icon" aria-hidden />
        <span>Pickup available</span>
      </span>
    );
  }
  if (durationText) {
    scheduleRowParts.push(
      <span key="dur" className="info-item-text">
        <FiClock size={18} className="info-icon" aria-hidden />
        <span>{durationText}</span>
      </span>
    );
  }
  if (hasTourGuideIncluded) {
    scheduleRowParts.push(
      <span key="guide" className="info-item-text">
        <BsHeadset size={18} className="info-icon" aria-hidden />
        <span>Tour guide included</span>
      </span>
    );
  }
  const typeLabel = type || 'Excursion';

  return (
    <Card className="tour-list-card pb-0 h-100">
      <div className="tour-list-card__image position-relative">
        {imageSrc ? (
          <Image src={imageSrc} className="card-img-top tour-list-card__img" alt={name || 'Tour'} loading="lazy" />
        ) : (
          <div className="card-img-top tour-list-card__img tour-list-card__img--empty" aria-hidden />
        )}
        {lunchIncludedText && (
          <div className="tour-list-card__lunch-badge">
            <span>{lunchIncludedText}</span>
          </div>
        )}
        <div className="tour-list-card__type-badge">
          <span>{typeLabel}</span>
        </div>
      </div>
      <CardBody className="tour-list-card__body">
        <div className="tour-list-card__title-row">
          <CardTitle as="h5" className="tour-list-card__title mb-0">
            <Link
              href={detailUrl}
              className="stretched-link"
              onClick={(event) => {
                if (typeof window === 'undefined') return;
                if (window.location.pathname === detailPath) return;
                event.preventDefault();
                window.location.assign(detailUrl);
              }}
            >
              {name}
            </Link>
          </CardTitle>
          <span className={`tour-list-card__title-type-icon-badge tour-list-card__title-type-icon-badge--${typeColorKey}`} aria-hidden>
            <TypeIcon />
          </span>
        </div>
        {tagline ? <p className="tour-list-card__tagline">{tagline}</p> : null}
        {urgency ? (
          <span
            className={`badge align-middle ${urgency.tone === 'danger' ? 'bg-danger-subtle text-danger' : 'bg-warning-subtle text-warning'}`}
            style={{ fontWeight: 600 }}
          >
            {urgency.text}
          </span>
        ) : null}
        {(scheduleRowParts.length > 0
          || wineOrBeerIncludedText
          || hasTourGuideIncluded) && (
          <div className="product-card-info-text">
            {scheduleRowParts.length > 0 && (
              <div className="product-card-info-row">{scheduleRowParts}</div>
            )}
            {wineOrBeerIncludedText && (
              <div className="product-card-info-row">
                <span className="info-item-text">
                  <FiCoffee size={18} className="info-icon" aria-hidden />
                  <span>{wineOrBeerIncludedText}</span>
                </span>
              </div>
            )}
          </div>
        )}
      </CardBody>
      <CardFooter className="tour-list-card__footer">
        <div className="tour-list-card__footer-row">
          <div className="tour-list-card__price-left">
            <span className="tour-list-card__price-unit me-1">From</span>
            <span className="tour-list-card__price-amount">
              {price != null && price !== '' ? (
                <>{currency}{Number(price).toFixed(2)}</>
              ) : (
                <span className="text-muted">Price on request</span>
              )}
            </span>
            {price != null && price !== '' && <span className="tour-list-card__price-unit">{pricingUnit === 'group' ? '/per group' : '/per person'}</span>}
          </div>
          <span className="tour-list-card__cta">
            View
            <FaArrowRight className="tour-list-card__cta-icon" aria-hidden />
          </span>
        </div>
      </CardFooter>
    </Card>
  );
};

const TYPE_LABEL_MAP = {
  'Guided Bus Tour': 'Bus Tour',
  'Guided Hiking Tour': 'Hiking Tour',
  'Guided Local Experience': 'Local Experience',
};
const TYPE_ICON_MAP = {
  'Bus Tour': 'bx-bus',
  'Hiking Tour': 'fas fa-hiking',
  'Boat Cruise': 'bxs-ship',
  'Semi-Private Cruise': 'bx-swim',
  'Private Cruise': 'bx-swim',
  'Jeep Safari': 'bx-landscape',
  'Local Experience': 'fas fa-leaf',
};
const TYPE_COLOR_MAP = {
  'Bus Tour': 'primary',
  'Hiking Tour': 'success',
  'Boat Cruise': 'info',
  'Semi-Private Cruise': 'warning',
  'Private Cruise': 'warning',
  'Jeep Safari': 'secondary',
  'Local Experience': 'danger',
};

function typeLabelForIcon(rawType) {
  const safeType = String(rawType || '').trim();
  return TYPE_LABEL_MAP[safeType] || safeType;
}

function getTypeColorKey(type) {
  return TYPE_COLOR_MAP[type] || 'secondary';
}

function getExcursionTypeIconComponent(iconName, normalizedType) {
  const normalizedIcon = String(iconName || TYPE_ICON_MAP[normalizedType] || '').trim().toLowerCase();

  if (normalizedIcon.includes('bus')) return FaBusAlt;
  if (normalizedIcon.includes('hiking') || normalizedIcon.includes('walk') || normalizedIcon.includes('run') || normalizedIcon.includes('cycling')) {
    return FaPersonHiking;
  }
  if (normalizedIcon.includes('ship') || normalizedIcon.includes('swim') || normalizedIcon.includes('water') || normalizedIcon.includes('cruise')) {
    return FaShip;
  }
  if (normalizedIcon.includes('leaf')) return FaLeaf;
  if (normalizedIcon.includes('landscape') || normalizedIcon.includes('map') || normalizedIcon.includes('trip') || normalizedIcon.includes('safari')) {
    return BsCompass;
  }

  return FaTag;
}

export default TourCard;
